import React from 'react';
import {Button, Card, Form, Slider, Typography} from "antd";
import BigNumber from "bignumber.js";
import utils from "../../common/utils";
import consts from "../../consts";
import FloatLabelNumberComponent from "../common/FloatLabelNumberComponent";
import {Translation} from "react-i18next";
import {debounce, find} from "lodash";
import LoadingSvg from "../common/LoadingSvg";
import ConfirmModal from '../modals/ConfirmModal';
import {number, object} from 'yup';
import BaseOrderComponent from "../common/BaseOrderComponent";

const {Text} = Typography;

class OrderSellLimit extends BaseOrderComponent {
  constructor(props) {
    super(props);
    this.getUserBalance = this.getUserBalance.bind(this);
  }
  
  async validate() {
    this.yupValidate = object().shape({
      price: number().required().moreThan(0).label(window.$t('price')),
      amount: number().required().moreThan(0).max(this.getUserBalance(), window.$t('Balance insufficient')).label(window.$t('amount')),
      total: number().required().moreThan(0).label(window.$t('total')),
    });
    return await super.validate();
  }
  
  createOrder = debounce(() => {
    this.hideModal();
    this.enLoading();
    this.props.onAddOrderAction({
      coin: this.props.coin,
      currency: this.props.currency,
      price: parseFloat(this.state.params.price),
      quantity: parseFloat(this.state.params.amount),
      trade_type: consts.ORDER_TRADE_TYPE_SELL,
      type: consts.ORDER_TYPE_LIMIT,
    }, () => {
      this.unLoading();
      this.clearOrder();
    });
  }, consts.DEFAULT_DURATION);
  
  componentWillReceiveProps(nextProps, nextContext) {
    if (nextProps.orderBook.isClickedOrderBookBuy) {
      this.setState({
        params: {
          price: nextProps.orderBook.price,
          amount: nextProps.orderBook.buyQuantity,
          total: nextProps.orderBook.buyTotal,
        }
      })
    } else if (nextProps.orderBook.isClickedOrderBookSell) {
      this.setState({
        params: {
          price: nextProps.orderBook.price,
          amount: 0,
          total: 0,
        }
      })
    }
  
    if (nextProps.coin !== this.props.coin || nextProps.currency !== this.props.currency) {
      // TODO: set last price
      this.handleMarketChange(nextProps);
    }
    
    if ((this.props.lastPrice !== nextProps.lastPrice) && !this.state.price) {
      this.setState({ params: { ...this.state.params, price: nextProps.lastPrice } });
    }
  }
  
  getUserBalance = () => {
    return window._.get(this.props.user.userBalances[this.props.coin], 'available_balance', 0);
  }
  
  clearOrder = (callback) => {
    this.setState({
      params: {
        price: 0,
        amount: 0,
        total: 0,
      }
    }, callback);
  }
  
  onSlide = (slideValue) => {
    this.onAmountChange(new BigNumber(this.getUserBalance()).div(100).times(slideValue).toString(10));
    this.setState({ slideValue });
  }
  
  render() {
    const CacheSlider = this.renderSlider;
    
    const layout = {
      wrapperCol: {span: 24},
    };
    const tailLayout = {
      wrapperCol: {span: 24},
    };
    const upperCurrency = `${this.props.currency}`.toUpperCase();
    const upperCoin = `${this.props.coin}`.toUpperCase();
  
    const pricePrecision = utils.getPrecision(this.coinSetting?.precision || 0.00000001);
    const amountPrecision = utils.getPrecision(this.coinSetting?.minimum_quantity_precision || 0.00000001);
    const totalPrecision = utils.getPrecision(this.coinSetting?.minimum_amount_precision || 0.00000001);
    
    return (
      <Translation>
        {(t) => <div className="order-sell-limit-wrap order-stop-limit-wrap">
          <Card className="order-card" loading={false}>
            <Form
              {...layout}
              name="basic"
            >
              <div className="mb-medium" style={{color: "#76808F"}}>
                {t('exchange.order_sell_limit.available')}: {utils.formatCurrency(utils.trimRightZeroAndDot(this.getUserBalance()))} {upperCoin}
              </div>
              <Form.Item rules={[{required: true, message: t('exchange.order_sell_limit.price_field_is_required')}]}>
                <FloatLabelNumberComponent
                  min={0}
                  customPrecision={pricePrecision}
                  name="price"
                  label={t('exchange.order_sell_limit.price')}
                  suffix={upperCurrency}
                  value={this.state.params.price}
                  onChange={(v) => this.onPriceChange(v, pricePrecision)}
                  overrideFocus={this.clearErrors}
                />
                <div className="text-left p-abs no-wrap">
                  <Text type='danger'>{utils.upperCaseFirst(this.state.errors.price || '')}</Text>
                </div>
              </Form.Item>
              <Form.Item rules={[{required: true, message: t('exchange.order_sell_limit.amount_field_is_required')}]}>
                <FloatLabelNumberComponent
                  min={0}
                  customPrecision={amountPrecision}
                  name="amount"
                  label={t('exchange.order_sell_limit.amount')}
                  suffix={upperCoin}
                  value={this.state.params.amount}
                  onChange={(v) => this.onAmountChange(v, amountPrecision)}
                  overrideFocus={this.clearErrors}
                />
                <div className="text-left p-abs no-wrap">
                  <Text type='danger'>{utils.upperCaseFirst(this.state.errors.amount || '')}</Text>
                </div>
              </Form.Item>
              <Form.Item rules={[{required: true, message: t('exchange.order_sell_limit.total_field_is_required')}]}
                         style={{marginTop: 20}}>
                <FloatLabelNumberComponent
                  min={0}
                  customPrecision={totalPrecision}
                  name="total"
                  label={t('exchange.order_sell_limit.total')}
                  suffix={upperCurrency}
                  value={this.state.params.total}
                  onChange={(v) => this.onTotalChange(v, totalPrecision)}
                  overrideFocus={this.clearErrors}
                />
                <div className="text-left p-abs no-wrap">
                  <Text type='danger'>{utils.upperCaseFirst(this.state.errors.total || '')}</Text>
                </div>
              </Form.Item>
  
              <CacheSlider slideValue={this.state.slideValue} />
              
              <Form.Item {...tailLayout}>
                <Button type="primary" htmlType="submit" onClick={this.showModal} className="bg-sell w-100"
                        disabled={this.state.isLoading}
                        icon={<LoadingSvg name='bars' hidden={!this.state.isLoading} size={20}/>}>
                  {!this.state.isLoading && `${t('exchange.order_sell_limit.sell')} ${upperCoin}`}
                </Button>
              </Form.Item>
            </Form>
            
            <ConfirmModal
              title={t('common.notification')}
              okText={t('common.confirm')}
              okType={'primary'}
              cancelText={t('Cancel')}
              onConfirm={this.createOrder}
              onCancel={this.hideModal}
              isVisible={this.state.isVisible}>
              <div>
                {t('exchange.order_confirm_question')}
              </div>
            </ConfirmModal>
          </Card>
        </div>}
      </Translation>
    );
  }
}

export default OrderSellLimit;
